import { useEffect } from "react";
import { BrowserRouter, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CookieConsent } from "@/components/CookieConsent";
import { ServicePopup } from "@/components/ServicePopup";
import Home from "@/pages/Home";
import ServicePage from "@/pages/ServicePage";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import PricingPage from "@/pages/PricingPage";
import LocationPage from "@/pages/LocationPage";
import CityPage from "@/pages/CityPage";
import LegalPage from "@/pages/LegalPage";
import Insights from "@/pages/Insights";
import ArticlePage from "@/pages/ArticlePage";
import Glossary from "@/pages/Glossary";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

export const lenisStore = { current: null };

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const lenis = lenisStore.current;
    if (hash) {
      const t = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) lenis ? lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView();
      }, 300);
      return () => clearTimeout(t);
    }
    lenis ? lenis.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const MarketingLayout = () => {
  const { pathname } = useLocation();
  const isServicePage = pathname.startsWith("/services/");
  return (
    <div className="noise-overlay min-h-screen bg-ink text-white">
      <Header />
      <main>
        <Outlet />
      </main>
      <Footer />
      <CookieConsent />
      {isServicePage && <ServicePopup />}
    </div>
  );
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    lenisStore.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisStore.current = null;
    };
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollManager />
        <Routes>
          <Route element={<MarketingLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/services/:slug" element={<ServicePage />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/locations/:slug" element={<LocationPage />} />
            <Route path="/locations/:slug/:city" element={<CityPage />} />
            <Route path="/terms" element={<LegalPage doc="terms" />} />
            <Route path="/privacy" element={<LegalPage doc="privacy" />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<ArticlePage />} />
            <Route path="/glossary" element={<Glossary />} />
          </Route>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="*" element={<Home />} />
        </Routes>
        <Toaster theme="dark" position="bottom-right" />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
